import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '@/axios/API';
import PropTypes from 'prop-types';
import { profileAvatars } from '@/constants/profile-avatar';
import { DataContext } from '../DataProviders/DataProvider';

const PrewarmContext = createContext();

export const EmailProviderType = {
    GOOGLE: 'GOOGLE',
    MICROSOFT: 'MICROSOFT'
};

// Separate arrays for male and female avatars using randomuser.me API
// Generate arrays with multiple random user images for variety (using numbers 1-99)
const maleAvatars = Array.from({ length: 50 }, (_, i) => ({
    id: i + 1,
    name: `Male Profile ${i + 1}`,
    image: `https://randomuser.me/api/portraits/men/${(i % 99) + 1}.jpg`
}));

const femaleAvatars = Array.from({ length: 50 }, (_, i) => ({
    id: i + 1,
    name: `Female Profile ${i + 1}`,
    image: `https://randomuser.me/api/portraits/women/${(i % 99) + 1}.jpg`
}));

export const PrewarmProvider = ({ children }) => {
    const { userRole, customerId, userData, loginCustomerId, userId } = useContext(DataContext);

    // Domain listing state
    const [prewarmStepCount, setPrewarmStepCount] = useState(0);
    const [domains, setDomains] = useState([]); // Transformed domains for user view
    const [rawDomains, setRawDomains] = useState([]); // Raw API data for admin view
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState(''); // Input value (for typing)
    const [activeSearchQuery, setActiveSearchQuery] = useState(''); // Actual search query used in API
    const [adminCounters, setAdminCounters] = useState(null);
    const [adminCountersLoading, setAdminCountersLoading] = useState(false);

    const [stats, setStats] = useState({
        availableDomains: 0,
        availableMailboxes: 0,
        averageDomainAge: 0
    });
    const [statsLoading, setStatsLoading] = useState(true);

    const [selectedProvider, setSelectedProvider] = useState(EmailProviderType.GOOGLE);

    // Checkout state - track if user has clicked checkout button
    const [hasClickedCheckout, setHasClickedCheckout] = useState(false);

    // Cart state - stored only in context, not in localStorage
    // Store full domain objects instead of just IDs to work across pages
    const [cartItems, setCartItems] = useState([]);

    // Selected domains for checkout (derived from cartItems)
    const [selectedDomains, setSelectedDomains] = useState([]);
    const [mailboxData, setMailboxData] = useState({});
    const [redirectUrls, setRedirectUrls] = useState({}); // redirectUrl per domain
    const [emailTool, setEmailTool] = useState({
        loginURL: "https://instantly.ai",
        username: "sm@demandlogy.com",
        password: "Question123#$",
        workspace: "Yes",
        email: "sm@demandlogy.com",
        shareAccessConfirmation: true,
        delegateAccessConfirmation: false
    });
    const [domainRegistrar, setDomainRegistrar] = useState({
        loginURL: 'https://www.namecheap.com/',
        username: 'sm@demandlogy.com',
        password: 'Question123#$',
        workspace: 'Yes',
        email: 'sm@demandlogy.com',
        shareAccessConfirmation: true,
        delegateAccessConfirmation: false
    });
    const [orderId, setOrderId] = useState(null);
    const [orderNumber, setOrderNumber] = useState(null);
    // Billing/Order data from API response
    const [orderData, setOrderData] = useState(null);
    const [billingData, setBillingData] = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [nextMonthTotalAmount, setNextMonthTotalAmount] = useState(0);
    const [thisMonthTotalAmount, setThisMonthTotalAmount] = useState(0);
    const [discountPrice, setDiscountPrice] = useState(0);
    const [taxAmount, setTaxAmount] = useState(0);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceId, setInvoiceId] = useState(null);
    const [userPaymentGateway, setUserPaymentGateway] = useState();

    // Fetch stats from counters API
    const fetchStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            const response = await API.get(`${import.meta.env.BACKEND_API}/pre/warm-up/counters?providerType=${selectedProvider}`);
            if (response.data) {
                setStats({
                    availableDomains: response.data.availableDomains || 0,
                    availableMailboxes: response.data.availableMailboxes || 0,
                    averageDomainAge: response.data.averageDomainAgeDays || 0
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            setStats({
                availableDomains: 0,
                availableMailboxes: 0,
                averageDomainAge: 0
            });
        } finally {
            setStatsLoading(false);
        }
    }, [selectedProvider]);

    // Fetch admin specific counters
    const fetchAdminCounters = useCallback(async () => {
        if (userRole !== 'ADMIN') return;

        try {
            setAdminCountersLoading(true);
            const response = await API.get(`${import.meta.env.BACKEND_API}/pre/warm-up/admin/counters`);
            if (response.data) {
                setAdminCounters(response.data);
            }
        } catch (error) {
            console.error('Error fetching admin counters:', error);
            setAdminCounters(null);
        } finally {
            setAdminCountersLoading(false);
        }
    }, [userRole]);

    console.log(userRole)
    // Fetch domains from API (with search support)
    const fetchDomains = useCallback(async () => {
        const CID = localStorage.getItem("loginCustomerId")
        try {
            setLoading(true);
            // Fetch stats from counters API immediately to show skeletons on tab switch
            fetchStats();
            // Use search API if activeSearchQuery exists, otherwise use get-list API
            let url;
            if (activeSearchQuery.trim()) {
                // Search API doesn't support status filter, so we'll filter client-side
                url = `${import.meta.env.BACKEND_API}/pre/warm-up/search?q=${encodeURIComponent(activeSearchQuery.trim())}&page=${0}&size=${pageSize}`;
            } else if (userRole === "USER") {
                url = `${import.meta.env.BACKEND_API}/pre/warm-up/get-list?page=${currentPage}&size=${pageSize}&status=CREATED&providerType=${selectedProvider}`;
            } else {
                url = `${import.meta.env.BACKEND_API}/pre/warm-up/get-list?page=${currentPage}&size=${pageSize}&providerType=${selectedProvider}`;
            }
            const response = await API.get(url);

            // Handle Spring Boot pagination structure
            let domainsData = [];
            let totalItems = 0;

            if (response.data?.content) {
                // Spring Boot pagination structure
                domainsData = response.data.content;
                // Keep original totalElements for pagination (represents total across all pages)
                totalItems = response.data.totalElements || 0;
            } else if (Array.isArray(response.data)) {
                // Direct array response (fallback)
                domainsData = response.data;
                totalItems = domainsData.length;
            }

            // Filter by status: For USER role, only show CREATED domains; for ADMIN, show all
            if (userRole === "USER") {
                domainsData = domainsData.filter(domain => domain.status === "CREATED");
                // Keep original totalItems from API for pagination
                // Note: For search API, totalItems includes all statuses, so pagination may show
                // more pages than actually have CREATED domains (limitation of client-side filtering)
                // For get-list API with status=CREATED, totalElements is already correct
            }

            // Transform API data to match component structure
            // Based on actual API response: { content: [{ id, domainName, emailAccounts, licenseCount, ... }] }
            const transformedDomains = domainsData.map((domain) => {
                const mailboxes = domain.licenseCount || domain.emailAccounts?.length || 0;
                const perBoxPrice = 4.99; // Default pricing - adjust based on your pricing logic
                const domainPrice = 15; // Default pricing - adjust based on your pricing logic
                const warmupWeeks = domain.pre_warmup_age || 1; // Use pre_warmup_age from API, fallback to 8
                const totalPrice = (perBoxPrice * mailboxes) + domainPrice;

                // Transform emailAccounts to mailboxDetails
                const mailboxDetails = (domain.emailAccounts || []).map((emailAccount, index) => {
                    // Get gender from emailAccount (M or F)
                    const gender = emailAccount.gender?.toUpperCase();

                    // Select appropriate avatar array based on gender
                    let selectedAvatarArray;
                    if (gender === 'M') {
                        // Male avatars: randomuser.me/api/portraits/men/1.jpg through men/99.jpg
                        selectedAvatarArray = maleAvatars;
                    } else if (gender === 'F') {
                        // Female avatars: randomuser.me/api/portraits/women/1.jpg through women/99.jpg
                        selectedAvatarArray = femaleAvatars;
                    } else {
                        // Default to all avatars if gender is not specified
                        selectedAvatarArray = profileAvatars;
                    }

                    // Use gender-specific avatar if profilePicture is null
                    // Use a more distributed selection based on email hash for better variety
                    const emailHash = emailAccount.email ?
                        emailAccount.email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : index;
                    const avatarIndex = emailHash % selectedAvatarArray.length;
                    const profilePicture = emailAccount.profilePicture || selectedAvatarArray[avatarIndex].image;

                    return {
                        id: index + 1,
                        emailAccountId: emailAccount.id, // Preserve original ID from API
                        name: emailAccount.fullName || `${emailAccount.firstName} ${emailAccount.lastName}`.trim(),
                        firstName: emailAccount.firstName || '',
                        lastName: emailAccount.lastName || '',
                        email: emailAccount.email || '',
                        username: emailAccount.email?.split('@')[0] || '',
                        role: emailAccount.isWorkspaceAdmin ? 'Admin' : null,
                        avatar: `${emailAccount.firstName?.charAt(0) || ''}${emailAccount.lastName?.charAt(0) || ''}`,
                        image: profilePicture,
                        profilePicture: profilePicture,
                        password: emailAccount.password,
                        recoveryEmail: emailAccount.recoveryEmail,
                        isWorkspaceAdmin: emailAccount.isWorkspaceAdmin,
                        gender: gender || emailAccount.gender,
                        emailProvider: emailAccount.emailProvider
                    };
                });

                return {
                    id: domain.id,
                    name: domain.domainName || '',
                    provider: domain.emailAccounts?.[0]?.emailProvider || 'GOOGLE',
                    mailboxes: mailboxes,
                    perBoxPrice: perBoxPrice,
                    domainPrice: domainPrice,
                    warmupWeeks: warmupWeeks,
                    totalPrice: totalPrice,
                    mailboxDetails: mailboxDetails,
                    licenseCount: domain.licenseCount,
                    status: domain.status,
                    createdAt: domain.createdAt,
                    updatedAt: domain.updatedAt
                };
            });

            // Store both raw and transformed data
            setRawDomains(domainsData); // Store raw data for admin view
            setDomains(transformedDomains); // Store transformed data for user view
            setTotalCount(totalItems);

            // Fetch stats from counters API
            await fetchStats();
        } catch (error) {
            console.error('Error fetching domains:', error);
            setDomains([]);
            setRawDomains([]);
            setTotalCount(0);
            // Still try to fetch stats even if domain fetching fails
            await fetchStats();
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, activeSearchQuery, fetchStats, userRole, selectedProvider]);

    // Note: fetchDomains is now called explicitly by components that need it
    // This prevents unnecessary API calls when admin pages are shown

    // Update selected domains when cart items change
    // cartItems now stores full domain objects, so we can use them directly
    useEffect(() => {
        if (cartItems.length > 0) {
            // cartItems now contains full domain objects
            setSelectedDomains(cartItems);

            // Initialize mailbox data with proper transformation
            // Preserve existing edited values when domains are removed/added
            setMailboxData(prevMailboxData => {
                const initialMailboxData = {};
                cartItems.forEach((domain, domainIndex) => {
                    // Get existing mailbox data for this domain (if any)
                    const existingDomainData = prevMailboxData[domain.id] || [];

                    initialMailboxData[domain.id] = domain.mailboxDetails.map((mb, mbIndex) => {
                        // Try to find existing mailbox data by emailAccountId to preserve edits
                        const existingMailbox = existingDomainData.find(
                            existing => existing.emailAccountId === mb.emailAccountId || existing.id === mb.id
                        );

                        // Handle both data structures: if name exists, split it; if firstName/lastName exist, use them
                        let firstName = mb.firstName;
                        let lastName = mb.lastName;

                        if (mb.name && !mb.firstName) {
                            // Split full name into first and last name
                            const nameParts = mb.name.trim().split(' ');
                            firstName = nameParts[0] || '';
                            lastName = nameParts.slice(1).join(' ') || '';
                        }

                        // Extract username from email if not provided
                        let username = mb.username;
                        if (!username && mb.email) {
                            username = mb.email.split('@')[0];
                        }

                        // Use image if available, otherwise use gender-based default avatar
                        let profilePicture = mb.image || mb.profilePicture;
                        if (!profilePicture) {
                            const gender = mb.gender?.toUpperCase();
                            let selectedAvatarArray;
                            if (gender === 'M') {
                                // Male avatars: randomuser.me/api/portraits/men/1.jpg through men/99.jpg
                                selectedAvatarArray = maleAvatars;
                            } else if (gender === 'F') {
                                // Female avatars: randomuser.me/api/portraits/women/1.jpg through women/99.jpg
                                selectedAvatarArray = femaleAvatars;
                            } else {
                                selectedAvatarArray = profileAvatars;
                            }
                            // Use email-based hash for consistent avatar assignment
                            const emailHash = mb.email ?
                                mb.email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) :
                                (domainIndex * domain.mailboxDetails.length + mbIndex);
                            const avatarIndex = emailHash % selectedAvatarArray.length;
                            profilePicture = selectedAvatarArray[avatarIndex].image;
                        }

                        return {
                            ...mb,
                            firstName: firstName, // Original firstName (for placeholder)
                            lastName: lastName, // Original lastName (for placeholder)
                            // Preserve existing edited values if they exist, otherwise initialize as empty
                            editedFirstName: existingMailbox?.editedFirstName || '', // User-edited firstName
                            editedLastName: existingMailbox?.editedLastName || '', // User-edited lastName
                            username: username, // Original username (for placeholder)
                            editedUsername: existingMailbox?.editedUsername || '', // User-edited username
                            // Preserve profile picture if it was uploaded/customized
                            profilePicture: existingMailbox?.profilePicture || profilePicture
                        };
                    });
                });
                return initialMailboxData;
            });
        } else {
            setSelectedDomains([]);
            setMailboxData({});
        }
    }, [cartItems]);

    // Cart management functions - no localStorage persistence
    // Now accepts and stores full domain objects instead of just IDs
    const addToCart = useCallback((domain) => {
        // Check if domain is already in cart by ID
        if (!cartItems.find(item => item.id === domain.id)) {
            const updated = [...cartItems, domain];
            setCartItems(updated);
        }
    }, [cartItems]);

    const removeFromCart = useCallback((domainId) => {
        const updated = cartItems.filter(item => item.id !== domainId);
        setCartItems(updated);
    }, [cartItems]);

    const clearCart = useCallback(() => {
        setCartItems([]);
        setSelectedDomains([]);
        setMailboxData({});
    }, []);

    // Clear all prewarm states (used on logout)
    const clearOrderDetails = useCallback(() => {
        setOrderId(null);
        setOrderNumber(null);
        setOrderData(null);
        setBillingData(null);
        setPromoCode('');
        setNextMonthTotalAmount(0);
        setThisMonthTotalAmount(0);
        setDiscountPrice(0);
        setTaxAmount(0);
        setInvoiceNumber('');
        setInvoiceId(null);
    }, []);

    const clearAllPrewarmData = useCallback(() => {
        // Clear cart and checkout data
        setCartItems([]);
        setSelectedDomains([]);
        setMailboxData({});
        setRedirectUrls({});
        setHasClickedCheckout(false);
        setPrewarmStepCount(0);

        // Clear order and billing data
        setOrderId(null);
        setOrderNumber(null);
        setOrderData(null);
        setBillingData(null);
        setPromoCode('');
        setNextMonthTotalAmount(0);
        setThisMonthTotalAmount(0);
        setDiscountPrice(0);
        setTaxAmount(0);
        setInvoiceNumber('');
        setInvoiceId(null);

        // Reset search and pagination
        setSearchQuery('');
        setActiveSearchQuery('');
        setCurrentPage(0);

        // Reset domains data
        setDomains([]);
        setRawDomains([]);
        setTotalCount(0);
        setStats({
            availableDomains: 0,
            availableMailboxes: 0,
            averageDomainAge: 0
        });

        // Reset email tool and domain registrar to defaults
        setEmailTool({
            loginURL: "https://instantly.ai",
            username: "sm@demandlogy.com",
            password: "Question123#$",
            workspace: "Yes",
            email: "sm@demandlogy.com",
            shareAccessConfirmation: true,
            delegateAccessConfirmation: false
        });
        setDomainRegistrar({
            loginURL: 'https://www.namecheap.com/',
            username: 'sm@demandlogy.com',
            password: 'Question123#$',
            workspace: 'Yes',
            email: 'sm@demandlogy.com',
            shareAccessConfirmation: true,
            delegateAccessConfirmation: false
        });
    }, []);

    const isDomainInCart = useCallback((domainId) => {
        return cartItems.some(item => item.id === domainId);
    }, [cartItems]);

    const getCartTotal = useCallback(() => {
        if (cartItems.length === 0) return 0;

        // cartItems now contains full domain objects, so we can calculate directly
        const total = cartItems.reduce((sum, domain) => {
            if (domain && domain.totalPrice) {
                return sum + domain.totalPrice;
            }
            return sum;
        }, 0);

        return total;
    }, [cartItems]);

    // Mailbox data management
    const updateMailboxData = useCallback((domainId, mailboxId, field, value) => {
        setMailboxData(prev => ({
            ...prev,
            [domainId]: prev[domainId]?.map(mb =>
                mb.id === mailboxId ? { ...mb, [field]: value } : mb
            ) || []
        }));
    }, []);

    // Note: Page reset on search is handled by the component when search button is clicked

    // Pagination
    const handlePageChange = useCallback((event, newPage) => {
        setCurrentPage(newPage);
        // fetchDomains will be called by component's useEffect when currentPage changes
    }, []);

    // Function to trigger search (called when search button is clicked)
    const triggerSearch = useCallback(() => {
        setActiveSearchQuery(searchQuery);
        setCurrentPage(0); // Reset to first page on new search
        // fetchDomains will be called by component's useEffect when activeSearchQuery changes
    }, [searchQuery]);

    // Function to clear search
    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setActiveSearchQuery('');
        setCurrentPage(0);
    }, []);

    // Function to update redirect URL for a domain
    const updateRedirectUrl = useCallback((domainId, url) => {
        setRedirectUrls(prev => ({
            ...prev,
            [domainId]: url
        }));
    }, []);

    // Function to call billing API and proceed to checkout
    const proceedToCheckout = useCallback(async (userId, loginCustomerId) => {
        try {
            setLoading(true);

            // Build emailAccounts array from mailboxData
            const emailAccounts = [];

            selectedDomains.forEach((domain) => {
                const domainMailboxes = mailboxData[domain.id] || [];
                const redirectUrl = redirectUrls[domain.id] || '';

                console.log(domainMailboxes, "domainMailboxes")

                domainMailboxes.forEach((mailbox) => {
                    // Use edited values if provided, otherwise fallback to original values
                    const finalFirstName = mailbox.editedFirstName?.trim() || mailbox.firstName || '';
                    const finalLastName = mailbox.editedLastName?.trim() || mailbox.lastName || '';
                    const finalUsername = mailbox.editedUsername?.trim() || mailbox.username || '';
                    // If username was edited, construct email from edited username, otherwise use original email
                    const finalEmail = mailbox.editedUsername?.trim()
                        ? `${mailbox.editedUsername.trim()}@${domain.name}`
                        : (mailbox.email || (finalUsername ? `${finalUsername}@${domain.name}` : ''));

                    emailAccounts.push({
                        id: mailbox.emailAccountId, // Include original ID from API
                        domain: domain.name,
                        email: finalEmail,
                        emailProvider: mailbox.emailProvider,
                        firstName: finalFirstName,
                        fullName: `${finalFirstName} ${finalLastName}`.trim(),
                        lastName: finalLastName,
                        profilePicture: mailbox.profilePicture || mailbox.image || '',
                        isWorkspaceAdmin: mailbox.isWorkspaceAdmin,
                        password: mailbox.password || '',
                        forwardingDomain: redirectUrl || '',
                        recoveryEmail: mailbox.recoveryEmail || ''
                    });
                });
            });

            const payload = {
                userId: userId,
                customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                orderId: null,
                orderNumber: null,
                organisationName: "Prewarm - " + userData.companyName,
                emailTool: emailTool,
                domainRegistrar: domainRegistrar,
                emailAccounts: emailAccounts
            };

            // First, call create-an-order API
            const response = await API.post(
                `${import.meta.env.BACKEND_API}/pre/warm-up/create-an-order`,
                payload
            );

            if (response.status === 200 && response.data) {
                // Extract orderId and orderNumber from response
                const orderDetails = response.data.data;

                const newOrderId = orderDetails.orderId;
                const newOrderNumber = orderDetails.orderNumber;

                // Update orderId and orderNumber state
                setOrderId(newOrderId);
                setOrderNumber(newOrderNumber);


                // Resolve User Payment Gateway
                const resolvePayload = {
                    customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                    userId: userId,
                    orderNumber: Number(newOrderNumber),
                    orderId: newOrderId,
                    currentMonthAmount: 0,
                    recurringAmount: 0,
                };

                try {
                    const gatewayResponse = await API.post(
                        `${import.meta.env.BACKEND_API}/resolve-user-payment-gateway`,
                        resolvePayload
                    );

                    if (gatewayResponse.status === 200) {
                        setUserPaymentGateway(gatewayResponse.data.paymentGateway);
                    }
                } catch (error) {
                    console.error("Error resolving payment gateway:", error);
                }

                // Now call billinglist API with the orderId and orderNumber
                const requestPayload = {
                    orderId: newOrderId,
                    userId,
                    orderNumber: Number(newOrderNumber),
                    customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                };

                // API request to billinglist
                let list = await API.post(
                    `${import.meta.env.BACKEND_API}/billinglist?orderType=PREWARMUP`,
                    requestPayload
                );

                // Use billinglist response for billing data
                const result = list.data[0];
                setOrderData(result);
                setBillingData(list.data);

                if (result?.orderId) {
                    setOrderId(result.orderId);
                }
                if (result?.orderNumber) {
                    setOrderNumber(result.orderNumber);
                }
                if (result?.invoiceNumber) {
                    setInvoiceNumber(result.invoiceNumber);
                }
                if (result?.id) {
                    setInvoiceId(result.id);
                }

                // Store billing amounts from billinglist response
                setDiscountPrice(result?.discountPrice || 0);
                setThisMonthTotalAmount(result?.totalAmount || 0);
                setNextMonthTotalAmount(result?.totalNextMonthAmount || 0);
                setTaxAmount(result?.taxAmount || 0);

                // Move to next step - increment step count
                setPrewarmStepCount(prev => prev + 1);
                setLoading(false);

                return { success: true, data: list.data };
            } else {
                setLoading(false);
                return { success: false, error: 'Invalid response from server' };
            }
        } catch (error) {
            console.error('Error calling billing API:', error);
            setLoading(false);
            return {
                success: false,
                error: error?.response?.data?.message || error?.message || 'Failed to proceed to checkout'
            };
        }
    }, [selectedDomains, mailboxData, redirectUrls, emailTool, domainRegistrar, orderId, orderNumber, userRole, customerId, userData]);

    const exportPrewarmData = useCallback(async (status = 'ALL') => {
        try {
            const response = await API.get(
                `${import.meta.env.BACKEND_API}/pre/warm-up/export`,
                {
                    params: { status }, // FIXED
                    responseType: 'blob',
                    validateStatus: () => true // handle manually
                }
            );

            const contentType = response.headers['content-type'];

            // 🔴 If backend sent error JSON instead of file
            if (contentType && contentType.includes('application/json')) {
                const text = await response.data.text();
                const errorJson = JSON.parse(text);

                throw new Error(errorJson.message || 'Export failed');
            }

            // ✅ Valid Excel file
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `prewarm_list_${status.toLowerCase()}_${Date.now()}.xlsx`;

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };

        } catch (error) {
            console.error('Export error:', error);

            return {
                success: false,
                error: error.message || 'Failed to export prewarm list'
            };
        }
    }, [loginCustomerId]);

    const value = {
        prewarmStepCount,
        setPrewarmStepCount,
        // Domain listing
        domains, // Transformed domains for user view
        rawDomains, // Raw API data for admin view
        loading,
        stats,
        totalCount,
        currentPage,
        setCurrentPage,
        pageSize,
        searchQuery,
        setSearchQuery,
        activeSearchQuery,
        triggerSearch,
        clearSearch,
        selectedProvider,
        setSelectedProvider,
        fetchDomains,
        fetchStats,
        handlePageChange,

        // Cart management
        cartItems,
        selectedDomains,
        addToCart,
        removeFromCart,
        clearCart,
        clearAllPrewarmData,
        isDomainInCart,
        getCartTotal,

        // Mailbox data
        mailboxData,
        setMailboxData,
        updateMailboxData,

        // Redirect URLs and additional data
        redirectUrls,
        updateRedirectUrl,
        emailTool,
        setEmailTool,
        domainRegistrar,
        setDomainRegistrar,

        // Order/Billing data
        orderId,
        orderNumber,
        orderData,
        billingData,
        proceedToCheckout,
        promoCode,
        setPromoCode,
        nextMonthTotalAmount,
        thisMonthTotalAmount,
        discountPrice,
        taxAmount,
        invoiceNumber,
        invoiceId,
        setLoading,
        hasClickedCheckout,
        setHasClickedCheckout,
        userPaymentGateway,
        adminCounters,
        adminCountersLoading,
        fetchAdminCounters,
        clearOrderDetails,
        exportPrewarmData,
    };

    return (
        <PrewarmContext.Provider value={value}>
            {children}
        </PrewarmContext.Provider>
    );
};

PrewarmProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const usePrewarm = () => {
    const context = useContext(PrewarmContext);
    if (!context) {
        throw new Error('usePrewarm must be used within a PrewarmProvider');
    }
    return context;
};

